import React, { useState, useRef, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Smartphone, Code, Rocket, ArrowRight, Menu, X, CheckCircle, Terminal, Cpu, Globe, ChevronRight, Github, Twitter, Linkedin, Mail } from 'lucide-react';
// --- Components ---
const HandDrawnCircle = () => <svg className="absolute -inset-2 w-[calc(100%+1rem)] h-[calc(100%+1rem)] pointer-events-none text-primary z-[-1]" viewBox="0 0 200 60" preserveAspectRatio="none" data-id="element-9">
    <path d="M10,30 Q50,5 90,30 T180,30 M10,30 Q50,55 90,30 T180,30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="opacity-80" strokeDasharray="400" strokeDashoffset="0" data-id="element-10">
      <animate attributeName="stroke-dashoffset" from="400" to="0" dur="1s" fill="freeze" data-id="element-11" />
    </path>
  </svg>;
const PhoneMockup = ({
  className,
  delay = 0
}: {
  className?: string;
  delay?: number;
}) => <motion.div className={`relative rounded-[2.5rem] border-8 border-zinc-800 bg-zinc-950 overflow-hidden shadow-2xl shadow-primary/10 ${className}`} initial={{
  y: 20,
  opacity: 0
}} animate={{
  y: [0, -15, 0],
  opacity: 1
}} transition={{
  y: {
    duration: 6,
    repeat: Infinity,
    ease: 'easeInOut',
    delay: delay
  },
  opacity: {
    duration: 0.8
  }
}} data-id="element-12">
    {/* Notch */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-32 bg-zinc-800 rounded-b-xl z-20" data-id="element-13"></div>

    {/* Screen Content */}
    <div className="w-full h-full bg-zinc-900 flex flex-col p-4 relative" data-id="element-14">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-30" data-id="element-15"></div>

      {/* Fake UI Elements */}
      <div className="w-full h-32 bg-zinc-800/50 rounded-xl mb-4 animate-pulse" data-id="element-16"></div>
      <div className="flex gap-2 mb-4" data-id="element-17">
        <div className="w-12 h-12 rounded-full bg-zinc-800/50" data-id="element-18"></div>
        <div className="flex-1 space-y-2 py-2" data-id="element-19">
          <div className="h-2 w-2/3 bg-zinc-800/50 rounded" data-id="element-20"></div>
          <div className="h-2 w-1/2 bg-zinc-800/50 rounded" data-id="element-21"></div>
        </div>
      </div>
      <div className="space-y-3" data-id="element-22">
        <div className="h-20 w-full bg-zinc-800/30 rounded-xl" data-id="element-23"></div>
        <div className="h-20 w-full bg-zinc-800/30 rounded-xl" data-id="element-24"></div>
        <div className="h-20 w-full bg-zinc-800/30 rounded-xl" data-id="element-25"></div>
      </div>

      {/* Floating Action Button */}
      <div className="absolute bottom-6 right-6 w-12 h-12 bg-primary rounded-full shadow-lg shadow-primary/30 flex items-center justify-center" data-id="element-26">
        <div className="w-6 h-6 text-white" data-id="element-27">
          <ArrowRight size={16} data-id="element-28" />
        </div>
      </div>
    </div>
  </motion.div>;
const SectionHeading = ({
  children,
  subtitle
}: {
  children: React.ReactNode;
  subtitle: string;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <div ref={ref} className="mb-16" data-id="element-29">
      <motion.span className="font-mono text-sm font-bold text-primary tracking-widest uppercase block mb-3" initial={{
      opacity: 0,
      x: -20
    }} animate={isInView ? {
      opacity: 1,
      x: 0
    } : {}} transition={{
      duration: 0.5
    }} data-id="element-30">
        {subtitle}
      </motion.span>
      <motion.h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight" initial={{
      opacity: 0,
      y: 20
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      delay: 0.1
    }} data-id="element-31">
        {children}
      </motion.h2>
    </div>;
};
const ServiceCard = ({
  icon: Icon,
  title,
  desc,
  index
}: {
  icon: any;
  title: string;
  desc: string;
  index: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="group p-8 rounded-2xl border border-white/5 bg-white/5 hover:bg-white/10 hover:border-primary/30 transition-all duration-300 relative overflow-hidden" data-id="element-32">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-33"></div>

      <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-300 border border-white/10 group-hover:border-primary/50 shadow-lg shadow-black/50" data-id="element-34">
        <Icon className="h-6 w-6" data-id="element-35" />
      </div>
      <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-36">
        {title}
      </h3>
      <p className="text-gray-400 leading-relaxed relative z-10 group-hover:text-gray-300 transition-colors" data-id="element-37">
        {desc}
      </p>
    </motion.div>;
};
export function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const {
    scrollYProgress
  } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  return <div className="min-h-screen bg-background font-sans text-text-main overflow-x-hidden" data-id="element-38">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5" data-id="element-39">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-40">
          <div className="flex justify-between items-center h-20" data-id="element-41">
            <div className="flex-shrink-0 flex items-center" data-id="element-42">
              <span className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-43">
                taptheory<span className="text-primary" data-id="element-44">.be</span>
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-45">
              <a href="#services" className="text-sm font-medium text-gray-400 hover:text-white transition-colors" data-id="element-46">
                Services
              </a>
              <a href="#work" className="text-sm font-medium text-gray-400 hover:text-white transition-colors" data-id="element-47">
                Work
              </a>
              <a href="#process" className="text-sm font-medium text-gray-400 hover:text-white transition-colors" data-id="element-48">
                Process
              </a>
              <a href="#contact" className="px-5 py-2.5 text-sm font-medium text-white bg-white/10 border border-white/10 rounded-lg hover:bg-white/20 hover:border-white/20 transition-all" data-id="element-49">
                Start Project
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center" data-id="element-50">
              <button onClick={toggleMenu} className="p-2 rounded-md text-gray-400 hover:text-white focus:outline-none" data-id="element-51">
                {isMenuOpen ? <X className="h-6 w-6" data-id="element-52" /> : <Menu className="h-6 w-6" data-id="element-53" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && <div className="md:hidden bg-surface border-b border-white/5" data-id="element-54">
            <div className="px-4 pt-2 pb-6 space-y-2" data-id="element-55">
              <a href="#services" className="block px-3 py-3 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-white/5" onClick={toggleMenu} data-id="element-56">
                Services
              </a>
              <a href="#work" className="block px-3 py-3 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-white/5" onClick={toggleMenu} data-id="element-57">
                Work
              </a>
              <a href="#process" className="block px-3 py-3 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-white/5" onClick={toggleMenu} data-id="element-58">
                Process
              </a>
              <a href="#contact" className="block px-3 py-3 rounded-md text-base font-medium text-primary font-bold hover:bg-white/5" onClick={toggleMenu} data-id="element-59">
                Start Project
              </a>
            </div>
          </div>}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-id="element-60">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" data-id="element-61"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-62"></div>

        {/* Massive Watermark */}
        <motion.div style={{
        opacity,
        scale
      }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none select-none z-0" data-id="element-63">
          <span className="text-[15vw] font-bold text-white/[0.02] tracking-tighter leading-none" data-id="element-64">
            taptheory.be
          </span>
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full grid lg:grid-cols-2 gap-12 items-center" data-id="element-65">
          <div className="max-w-2xl" data-id="element-66">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm" data-id="element-67">
              <span className="flex h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse" data-id="element-68"></span>
              <span className="font-mono text-xs font-medium text-gray-300 tracking-wider uppercase" data-id="element-69">
                Available for new projects
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-8 leading-[1.1]" data-id="element-70">
              Freelance <br data-id="element-71" />
              <span className="relative inline-block text-white" data-id="element-72">
                mobile
                <HandDrawnCircle data-id="element-73" />
              </span>{' '}
              development
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-xl text-gray-400 mb-10 max-w-lg leading-relaxed" data-id="element-74">
              Crafting immersive, high-performance iOS and Android applications
              that stand out in the App Store.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 items-start sm:items-center" data-id="element-75">
              <a href="#contact" className="group relative inline-flex justify-center items-center px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary to-primary-dark rounded-full hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all duration-300 overflow-hidden" data-id="element-76">
                <span className="relative z-10 flex items-center" data-id="element-77">
                  Start Your Project{' '}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-78" />
                </span>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-79"></div>
              </a>

              {/* Stats Row Inline */}
              <div className="hidden sm:flex items-center gap-4 px-6 py-2 border-l border-white/10 ml-2" data-id="element-80">
                <span className="font-mono text-sm text-gray-400" data-id="element-81">
                  <span className="text-primary font-bold" data-id="element-82">50+</span> apps
                </span>
                <span className="w-1 h-1 bg-gray-600 rounded-full" data-id="element-83"></span>
                <span className="font-mono text-sm text-gray-400" data-id="element-84">
                  <span className="text-primary font-bold" data-id="element-85">8</span> years
                </span>
                <span className="w-1 h-1 bg-gray-600 rounded-full" data-id="element-86"></span>
                <span className="font-mono text-sm text-gray-400" data-id="element-87">
                  iOS & Android
                </span>
              </div>
            </motion.div>
          </div>

          {/* Floating Mockups */}
          <div className="relative h-[600px] hidden lg:block perspective-1000" data-id="element-88">
            <PhoneMockup className="absolute top-10 right-20 w-64 h-[500px] z-20 rotate-[-6deg]" delay={0} data-id="element-89" />
            <PhoneMockup className="absolute top-32 right-0 w-64 h-[500px] z-10 rotate-[6deg] opacity-60 scale-90 blur-[1px]" delay={2} data-id="element-90" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 relative" data-id="element-91">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-92">
          <SectionHeading subtitle="Expertise" data-id="element-93">
            Technical mastery <br data-id="element-94" />{' '}
            <span className="text-gray-500" data-id="element-95">for modern products</span>
          </SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-96">
            <ServiceCard index={0} icon={Smartphone} title="Native Mobile Apps" desc="High-performance iOS (Swift) and Android (Kotlin) applications built with native tools for the best possible user experience and device integration." data-id="element-97" />
            <ServiceCard index={1} icon={Code} title="Cross-Platform" desc="Efficient React Native and Flutter development for rapid deployment across both platforms without compromising on quality or performance." data-id="element-98" />
            <ServiceCard index={2} icon={Rocket} title="Product Strategy" desc="From initial concept to App Store launch. We handle the technical roadmap, architecture, and submission process so you can focus on business." data-id="element-99" />
          </div>
        </div>
      </section>

      {/* Client Logos Section - hidden for now
          <section className="py-20 border-y border-white/5 bg-white/[0.02]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="text-center font-mono text-sm text-gray-500 mb-12">
                TRUSTED BY INNOVATIVE COMPANIES
              </p>
                 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-12">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="group flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-500 opacity-40 hover:opacity-100"
                  >
                    <div className="h-12 w-32 bg-white/10 rounded animate-pulse group-hover:bg-primary/20 transition-colors"></div>
                  </div>
                ))}
              </div>
            </div>
          </section> */}

      {/* Process/Approach Section */}
      <section id="process" className="py-32 relative overflow-hidden" data-id="element-100">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent pointer-events-none" data-id="element-101"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-102">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-103">
            <div data-id="element-104">
              <SectionHeading subtitle="Approach" data-id="element-105">
                Built for scale, <br data-id="element-106" />{' '}
                <span className="text-gray-500" data-id="element-107">designed for users</span>
              </SectionHeading>

              <div className="space-y-8" data-id="element-108">
                {[{
                title: 'Clean Architecture',
                desc: "Modular, testable code that's easy to maintain and scale."
              }, {
                title: 'User-Centric Design',
                desc: 'Interfaces that feel natural and intuitive on every device.'
              }, {
                title: 'Performance First',
                desc: 'Optimized for battery, data usage, and 60fps animations.'
              }].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex gap-4" data-id="element-109">
                    <div className="flex-shrink-0 mt-1" data-id="element-110">
                      <CheckCircle className="w-6 h-6 text-primary" data-id="element-111" />
                    </div>
                    <div data-id="element-112">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-113">
                        {item.title}
                      </h3>
                      <p className="text-gray-400" data-id="element-114">{item.desc}</p>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            <div className="relative" data-id="element-115">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 blur-3xl rounded-full opacity-30" data-id="element-116"></div>
              <div className="relative bg-zinc-900 border border-white/10 rounded-2xl p-6 shadow-2xl" data-id="element-117">
                <div className="flex items-center gap-2 mb-4 border-b border-white/5 pb-4" data-id="element-118">
                  <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-119"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-120"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-121"></div>
                  <span className="ml-2 font-mono text-xs text-gray-500" data-id="element-122">
                    App.tsx
                  </span>
                </div>
                <div className="font-mono text-sm space-y-2" data-id="element-123">
                  <div className="text-purple-400" data-id="element-124">
                    import <span className="text-white" data-id="element-125">React</span> from{' '}
                    <span className="text-green-400" data-id="element-126">'react'</span>
                  </div>
                  <div className="text-purple-400" data-id="element-127">
                    import <span className="text-white" data-id="element-128">{'{ motion }'}</span>{' '}
                    from <span className="text-green-400" data-id="element-129">'framer-motion'</span>
                  </div>
                  <br data-id="element-130" />
                  <div className="text-blue-400" data-id="element-131">
                    export function <span className="text-yellow-400" data-id="element-132">App</span>
                    () {'{'}
                  </div>
                  <div className="pl-4 text-gray-300" data-id="element-133">return (</div>
                  <div className="pl-8 text-gray-300" data-id="element-134">{'<motion.div'}</div>
                  <div className="pl-12 text-purple-400" data-id="element-135">
                    initial=
                    <span className="text-blue-300" data-id="element-136">{'{{ opacity: 0 }}'}</span>
                  </div>
                  <div className="pl-12 text-purple-400" data-id="element-137">
                    animate=
                    <span className="text-blue-300" data-id="element-138">{'{{ opacity: 1 }}'}</span>
                  </div>
                  <div className="pl-8 text-gray-300" data-id="element-139">{'>'}</div>
                  <div className="pl-12 text-gray-400" data-id="element-140">
                    {'/* Magic happens here */'}
                  </div>
                  <div className="pl-12 text-white" data-id="element-141">{'<Experience />'}</div>
                  <div className="pl-8 text-gray-300" data-id="element-142">{'</motion.div>'}</div>
                  <div className="pl-4 text-gray-300" data-id="element-143">)</div>
                  <div className="text-blue-400" data-id="element-144">{'}'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-32 relative overflow-hidden" data-id="element-145">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-zinc-900/50 to-background pointer-events-none" data-id="element-146"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-147">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-148">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-149">
              Ready to build <br data-id="element-150" /> something{' '}
              <span className="text-primary" data-id="element-151">extraordinary?</span>
            </h2>
            <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-152">
              Let's turn your concept into a polished, production-ready
              application.
            </p>

            <div className="bg-white/5 backdrop-blur-xl p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl max-w-xl mx-auto text-left" data-id="element-153">
              <form className="space-y-6" data-id="element-154">
                <div data-id="element-155">
                  <label htmlFor="email" className="block text-xs font-mono text-primary mb-2 uppercase tracking-wider" data-id="element-156">
                    Email Address
                  </label>
                  <input type="email" id="email" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="hello@company.com" data-id="element-157" />
                </div>
                <div data-id="element-158">
                  <label htmlFor="message" className="block text-xs font-mono text-primary mb-2 uppercase tracking-wider" data-id="element-159">
                    Project Details
                  </label>
                  <textarea id="message" rows={4} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none" placeholder="Tell us about your idea..." data-id="element-160"></textarea>
                </div>
                <button type="button" onClick={() => {
                const email = (document.getElementById('email') as HTMLInputElement)?.value || '';
                const message = (document.getElementById('message') as HTMLTextAreaElement)?.value || '';
                const subject = encodeURIComponent('New project inquiry');
                const body = encodeURIComponent(`From: ${email}\n\nMessage:\n${message}`);
                window.location.href = `mailto:hello@taptheory.be?subject=${subject}&body=${body}`;
              }} className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-1" data-id="element-161">
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 bg-black" data-id="element-162">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-163">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-164">
            <div className="flex items-center" data-id="element-165">
              <span className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-166">
                taptheory<span className="text-primary" data-id="element-167">.be</span>
              </span>
            </div>

            <div className="flex space-x-8 text-sm font-medium text-gray-400" data-id="element-168">
              <Link to="/contact" className="hover:text-primary transition-colors" data-id="element-169">
                Contact
              </Link>
              <Link to="/privacy" className="hover:text-primary transition-colors" data-id="element-170">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-primary transition-colors" data-id="element-171">
                Terms
              </Link>
            </div>

            <div className="flex space-x-6" data-id="element-172">
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-173">
                <Github className="w-5 h-5" data-id="element-174" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-175">
                <Twitter className="w-5 h-5" data-id="element-176" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-177">
                <Linkedin className="w-5 h-5" data-id="element-178" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-179">
                <Mail className="w-5 h-5" data-id="element-180" />
              </a>
            </div>
          </div>
          <div className="mt-8 text-center text-xs text-gray-600 font-mono" data-id="element-181">
            &copy; {new Date().getFullYear()} Tap Theory. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}